import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Grid2,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import SkillVationAuthLayout from "../../../layout/SkillVationAuthLayout";
import { useGetSchoolsByPincode } from "../../../assets/nodeApi/getApi/getSchoolApi";
import {
  postSchoolSignup,
  postTeacherSignUp,
} from "../../../assets/nodeApi/postApi/postSchoolApi";
import useOtpVerification from "./useOtpVerification";
import OtpModal from "./OtpModal";
import useAuthStore from "../../../shared/store/useAuthStore";

const textFieldStyle = {
  "& .MuiOutlinedInput-root": {
    height: 48,
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    "& fieldset": { borderColor: "#9D9D9D", borderWidth: "1px" },
    "&:hover fieldset": { borderColor: "#9D9D9D" },
    "&.Mui-focused": { backgroundColor: "#FFFFFF" },
    "&.Mui-focused fieldset": { borderColor: "#4E93E1", borderWidth: "2px" },
    "&.Mui-disabled": { backgroundColor: "#F5F5F5" },
    "&.Mui-disabled fieldset": { borderColor: "#9D9D9D" },
  },
  "& .MuiInputBase-input": { padding: "12px 14px" },
};

const submitButtonStyle = {
  height: 48,
  borderRadius: "8px",
  backgroundColor: "#4E93E1",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "16px",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#2563EB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#4E93E1", opacity: 0.6, color: "#fff" },
  color: "#fff",
};

const CommonRegisterForm = ({ bottomLine, role }) => {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [addedPincode, setAddedPincode] = useState("");
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [serverError, setServerError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { data: schoolData, isFetching: isLoadingPreschool } =
    useGetSchoolsByPincode(addedPincode);

  const preSchoolOptions = schoolData?.data || [];

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      username: "",
      mobile: "",
      password: "",
      confirm_password: "",
      zip: "",
      school_id: "",
    },
  });

  const otpHook = useOtpVerification({
    role,
    onVerified: () => clearErrors("mobile"),
    onError: (msg) => setServerError(msg),
  });

  // ── Reset mobile so user can re-enter and re-verify ──────────────────
  const handleEditMobile = () => {
    setValue("mobile", "");
    setServerError("");
    clearErrors("mobile");
    otpHook.resetOtp();
  };

  const handlePincode = (pincode) => {
    setAddedPincode(pincode.length === 6 ? pincode : "");
  };

  const handleSchoolChange = (school) => {
    setSelectedSchool(school);
    setValue("school_id", school?.id || "");
    // Clear any school_id errors when a school is selected
    if (school) {
      clearErrors("school_id");
    }
  };

  // UPDATED: Add validation for school selection
  const handleVerifyClick = () => {
    // For school role, validate school selection first
    if (role === "school") {
      // Check if school_id is selected (either from form or selectedSchool state)
      const schoolId = watch("school_id");
      if (!schoolId || !selectedSchool) {
        setError("school_id", { 
          message: "Please select a school before verifying mobile number" 
        });
        return;
      }
      // Clear any existing school_id errors if validation passes
      clearErrors("school_id");
    }
    
    // Validate mobile number format before sending OTP
    const mobile = watch("mobile");
    if (!mobile || !/^[0-9]{10}$/.test(mobile)) {
      setError("mobile", { 
        message: "Please enter a valid 10-digit mobile number" 
      });
      return;
    }
    
    // Clear mobile errors and proceed with OTP
    clearErrors("mobile");
    otpHook.sendOtp(mobile, setError);
  };

  const handleRegister = async (data) => {
    setServerError("");

    if (!otpHook.isVerified) {
      setError("mobile", { message: "Please verify your mobile number first" });
      return;
    }
    if (data.password !== data.confirm_password) {
      setError("confirm_password", { message: "Passwords do not match" });
      return;
    }

    try {
      if (role === "school") {
        const { confirm_password, ...rest } = data;
        const res = await postSchoolSignup({
          firstname: rest.firstname,
          lastname: rest.lastname,
          email: rest.email,
          username: rest.username,
          mobile: `${rest.mobile}`,
          password: rest.password,
          zip: rest.zip,
          school_id: Number(rest.school_id),
        });

        login(res?.data, true);

        const storedPath = localStorage.getItem("lastVisitedPath");
        navigate(storedPath || "/", { replace: true });
        setTimeout(() => localStorage.removeItem("lastVisitedPath"), 400);
        return;

      } else if (role === "teacher") {
        const { confirm_password, zip, school_id, ...rest } = data;
        let mobile = rest.mobile.trim().replace(/\D/g, "");
        if (mobile.length > 10) mobile = mobile.slice(-10);

        const res = await postTeacherSignUp({
          firstname: rest.firstname.trim(),
          lastname: rest.lastname.trim(),
          email: rest.email.toLowerCase().trim(),
          username: rest.username.toLowerCase().trim(),
          mobile,
          password: rest.password,
        });

        login(res?.data, true);
        const storedPath = localStorage.getItem("lastVisitedPath");
        navigate(storedPath || "/", { replace: true });
        setTimeout(() => localStorage.removeItem("lastVisitedPath"), 400);
        return;

      } else {
        throw new Error("Invalid role specified");
      }

      const storedPath = localStorage.getItem("lastVisitedPath") || "/";
      navigate(storedPath, { replace: true });

    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      setServerError(msg);
    }
  };

  return (
    <SkillVationAuthLayout
      title="Welcome!!"
      subtitle={
        role === "school"
          ? "School Signup to add courses and events for your community"
          : "Teacher Signup to share your knowledge and skills"
      }
      bottomText="Already have an account?"
      bottomLinkLabel="Sign In"
      bottomLinkTo="/login"
    >
      <Box
        component="form"
        onSubmit={handleSubmit(handleRegister)}
        display="flex"
        flexDirection="column"
        gap={2.5}
      >
        {/* Row 1 — First Name & Last Name */}
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
              First Name <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your first name"
              error={!!errors.firstname}
              helperText={errors.firstname?.message}
              {...register("firstname", { required: "First name is required" })}
              sx={textFieldStyle}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
              Last Name <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your last name"
              error={!!errors.lastname}
              helperText={errors.lastname?.message}
              {...register("lastname", { required: "Last name is required" })}
              sx={textFieldStyle}
            />
          </Grid2>
        </Grid2>

        {/* Row 2 — Email & Username */}
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
              E-Mail <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="email"
              placeholder="Enter your email"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
              sx={textFieldStyle}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
              User Name <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your username"
              error={!!errors.username}
              helperText={errors.username?.message}
              {...register("username", { required: "Username is required" })}
              sx={textFieldStyle}
            />
          </Grid2>
        </Grid2>

        {/* School-specific fields */}
        {role === "school" && (
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Pincode <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter pincode"
                error={!!errors.zip}
                helperText={errors.zip?.message}
                {...register("zip", {
                  required: "Pincode is required",
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: "Enter a valid 6-digit pincode",
                  },
                })}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setValue("zip", value);
                  handlePincode(value);
                }}
                sx={textFieldStyle}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                School <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <Autocomplete
                options={preSchoolOptions}
                getOptionLabel={(option) => option.school_display_name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingPreschool}
                value={selectedSchool}
                onChange={(_, newValue) => handleSchoolChange(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select your school"
                    error={!!errors.school_id}
                    helperText={errors.school_id?.message}
                    sx={textFieldStyle}
                  />
                )}
              />
              <Box textAlign="center" mt={1}>
                <IconButton
                  onClick={() => navigate("/PreSchoolOnboard")}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  <Typography variant="body2" color="primary.main">
                    Didn't find your school?
                  </Typography>
                </IconButton>
              </Box>
            </Grid2>
          </Grid2>
        )}

        {/* Mobile with Verify / Edit */}
        <Box>
          <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
            Mobile Number <span style={{ color: "#ef4444" }}>*</span>
          </Typography>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              type="tel"
              placeholder="Enter mobile number"
              error={!!errors.mobile}
              helperText={errors.mobile?.message}
              disabled={otpHook.isVerified}
              {...register("mobile", {
                required: "Mobile number is required",
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Enter a valid 10-digit mobile number",
                },
              })}
              sx={textFieldStyle}
            />

            {/* Verified state — show green badge + Edit icon */}
            {otpHook.isVerified ? (
              <Box display="flex" alignItems="flex-start" gap={0.5}>
                <Button
                  disabled
                  sx={{
                    height: 48,
                    px: 2,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    borderColor: "#22c55e",
                    color: "#16a34a",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #22c55e",
                    "&.Mui-disabled": {
                      borderColor: "#22c55e",
                      color: "#16a34a",
                      backgroundColor: "#f0fdf4",
                    },
                  }}
                >
                  ✓ Verified
                </Button>
                <IconButton
                  onClick={handleEditMobile}
                  title="Edit mobile number"
                  sx={{
                    height: 48,
                    width: 48,
                    borderRadius: "8px",
                    border: "1px solid #9D9D9D",
                    color: "#6B7280",
                    flexShrink: 0,
                    "&:hover": { borderColor: "#4E93E1", color: "#4E93E1", backgroundColor: "#eff6ff" },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                type="button"
                variant="outlined"
                onClick={handleVerifyClick}
                disabled={otpHook.isSendPending}
                sx={{
                  height: 48,
                  px: 3,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  alignSelf: "flex-start",
                  borderColor: "#4E93E1",
                  color: "#4E93E1",
                  "&:hover": { backgroundColor: "#eff6ff", borderColor: "#4E93E1" },
                }}
              >
                {otpHook.isSendPending ? "Sending…" : "Verify"}
              </Button>
            )}
          </Box>
        </Box>

        {/* Server Error — with inline Edit link when it's a duplicate error */}
        {serverError && (
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2" color="#DC143C" fontSize="14px">
              {serverError}
            </Typography>
            {serverError.toLowerCase().includes("already exists") && (
              <Typography
                variant="body2"
                fontSize="14px"
                color="#4E93E1"
                fontWeight={400}
                onClick={handleEditMobile}
                sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              >
                Edit mobile number
              </Typography>
            )}
          </Box>
        )}

        {/* Password fields — shown only after OTP verified */}
        {otpHook.isVerified && (
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Password <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                error={!!errors.password}
                helperText={errors.password?.message}
                {...register("password", {
                  required: "Password is required",
                  minLength: { value: 8, message: "At least 8 characters" },
                })}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                        sx={{ color: "#9D9D9D", "&:hover": { color: "#4E93E1" } }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={400} mb={1} color="text.primary" fontSize="14px">
                Confirm Password <span style={{ color: "#ef4444" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                error={!!errors.confirm_password}
                helperText={errors.confirm_password?.message}
                {...register("confirm_password", {
                  required: "Please confirm your password",
                  validate: (val) =>
                    val === watch("password") || "Passwords do not match",
                })}
                sx={textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        edge="end"
                        sx={{ color: "#9D9D9D", "&:hover": { color: "#4E93E1" } }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid2>
          </Grid2>
        )}

        {/* Submit */}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isSubmitting || !otpHook.isVerified}
          sx={submitButtonStyle}
        >
          {isSubmitting ? "Signing Up…" : "Sign Up"}
        </Button>

        <Grid2 size={12}>{bottomLine}</Grid2>
      </Box>

      <OtpModal
        mobile={watch("mobile")}
        open={otpHook.showOtpModal}
        onClose={otpHook.closeModal}
        otp={otpHook.otp}
        setOtp={otpHook.setOtp}
        otpError={otpHook.otpError}
        isVerifyPending={otpHook.isVerifyPending}
        isResendPending={otpHook.isResendPending}
        canResend={otpHook.canResend}
        countdown={otpHook.countdown}
        verifyOtp={otpHook.verifyOtp}
        resendOtp={otpHook.resendOtp}
      />
    </SkillVationAuthLayout>
  );
};

export default CommonRegisterForm;